export const transformMaterialList = (list) => {
  const allowedTypes = [1209, 1210, 18738];
  const seenAttachmentIds = new Set();

  const updateDates = (item, attachment) => {
    const { receivedAt, releaseDate } = attachment;

    if (
      !item.lastAttachmentReceiveDate ||
      new Date(receivedAt) > new Date(item.lastAttachmentReceiveDate)
    ) {
      item.lastAttachmentReceiveDate = receivedAt;
    }

    if (
      !item.lastAttachmentReleaseDate ||
      new Date(releaseDate) > new Date(item.lastAttachmentReleaseDate)
    ) {
      item.lastAttachmentReleaseDate = releaseDate;
    }
  };

  return list.reduce((acc, item) => {
    let itemIncluded = false;

    item.attachments.forEach(({ attachment, olderVersions }) => {
      const attachmentId = attachment.id;
      const attachmentTypeId = attachment.attachmentType.id;

      if (
        allowedTypes.includes(attachmentTypeId) &&
        !seenAttachmentIds.has(attachmentId)
      ) {
        seenAttachmentIds.add(attachmentId);
        const version = attachment.version;

        let existingItem = acc.find(
          (i) =>
            i.attachmentType.id === attachmentTypeId && i.version === version
        );

        if (!existingItem) {
          const newItem = {
            ...item,
            attachmentType: attachment.attachmentType,
            version,
            attachments: [{ attachment, olderVersions: [] }],
            lastAttachmentReceiveDate: attachment.receivedAt,
            lastAttachmentReleaseDate: attachment.releaseDate,
          };
          acc.push(newItem);
        } else {
          existingItem.attachments[0].olderVersions.push(attachment);
          updateDates(existingItem, attachment);
        }
        itemIncluded = true;
      }

      if (olderVersions) {
        olderVersions.forEach((olderAttachment) => {
          const olderId = olderAttachment.id;
          const olderTypeId = olderAttachment.attachmentType.id;

          if (
            allowedTypes.includes(olderTypeId) &&
            !seenAttachmentIds.has(olderId)
          ) {
            seenAttachmentIds.add(olderId);
            const olderVersion = olderAttachment.version;

            let existingOlderItem = acc.find(
              (i) =>
                i.attachmentType.id === olderTypeId &&
                i.version === olderVersion
            );

            if (!existingOlderItem) {
              const newOlderItem = {
                ...item,
                attachmentType: olderAttachment.attachmentType,
                version: olderVersion,
                attachments: [
                  { attachment: olderAttachment, olderVersions: [] },
                ],
                lastAttachmentReceiveDate: olderAttachment.receivedAt,
                lastAttachmentReleaseDate: olderAttachment.releaseDate,
              };
              acc.push(newOlderItem);
            } else {
              existingOlderItem.attachments[0].olderVersions.push(
                olderAttachment
              );
              updateDates(existingOlderItem, olderAttachment);
            }
            itemIncluded = true;
          }
        });
      }
    });

    if (!itemIncluded && allowedTypes.includes(item?.attachmentType?.id)) {
      acc.push({
        ...item,
        attachments: [{ attachment: item.attachments, olderVersions: [] }],
        lastAttachmentReceiveDate: null,
        lastAttachmentReleaseDate: null,
      });
    }

    return acc;
  }, []);
};

export const compileFiles = (attachments) => {
  if (!attachments || !Array.isArray(attachments)) return [];

  return attachments
    .flatMap(({ attachment, olderVersions = [] }) => [
      attachment,
      ...olderVersions,
    ])
    .filter((file) => !!file?.fileId);
};

export const totalEpisodesCounter = (planning) => {
  let episodes = [];
  const allEpisodes = planning.data?.episodes;
  if (allEpisodes?.length) {
    allEpisodes.forEach((episode) => {
      const isEpisodeNotIncluded = !episodes.includes(episode.episodeNumber);
      if (isEpisodeNotIncluded) episodes = [...episodes, episode.episodeNumber];
    });
  }
  return episodes.length;
};

export const returnNextValidVersionNumber = ({
  versionsList = [],
  typeId,
  currentEpisodeNumber = 1,
}) => {
  if (!versionsList?.length) return 1;

  const isEpisode = Number(typeId) === 1211;

  const filteredList = versionsList.filter((item) => {
    if (Number(item.type.id) !== Number(typeId)) return false;

    if (isEpisode) {
      return Number(item.episodeNumber) === Number(currentEpisodeNumber);
    }

    return true;
  });

  if (!filteredList.length) return 1;

  const maxVersion = Math.max(
    ...filteredList.map((item) => Number(item.version) || 0)
  );

  return maxVersion + 1;
};

export const returnNextValidVersionNumberForEpisodes = ({
  versionsList = [],
  currentEpisodeNumber = 1,
}) => {
  if (!versionsList) return 1;

  const episodesPlanning = versionsList?.filter(
    (material) => material.type.id === 1211
  );

  if (!episodesPlanning?.length) return 1;

  const currentEpisodeVersions = episodesPlanning?.filter(
    (episode) => episode.episodeNumber === currentEpisodeNumber
  );

  if (!currentEpisodeVersions?.length) return 1;

  const lastEpisodeNumber = Math.max(
    0,
    ...currentEpisodeVersions?.map((v) => v?.version || 0)
  );

  return lastEpisodeNumber + 1;
};

export const returnNextValidEpisodeNumber = ({ versionsList = [] }) => {
  if (!versionsList?.length) return 1;

  const episodes = versionsList?.filter(
    (item) => Number(item?.type?.id) === 1211
  );

  if (!episodes?.length) return 1;

  const maxEpisode = Math.max(
    ...episodes.map((item) => Number(item.episodeNumber) || 0)
  );

  return maxEpisode + 1;
};

export const createEpisodesMenuOptions = (number) => {
  return Array.from({ length: number }, (_, i) => ({
    id: i + 1,
    name: String(i + 1),
  }));
};

export const returnAttachmentTypesListByContentType = (
  contentType,
  thesaurusList,
  schedulerType
) => {
  const CHAPTER_AND_EPISODE_ATTACHMENT_TYPES = [
    { id: 1211, name: "Capítulo" },
    { id: 1178, name: "Sinopse" },
  ];

  const RENAMED_DRAMATURGY_ATTACHMENT_TYPES = [
    { id: 1209, name: "Argumento" },
    { id: 1211, name: "Episódio" },
    { id: 1178, name: "Sin. Temporada" },
    ...thesaurusList,
  ];

  const RENAMED_VARIETIES_ATTACHMENT_TYPES = [
    { id: 601, name: "Episódio" },
    { id: 1209, name: "Argumento" },
    ...thesaurusList,
  ];

  if (schedulerType === "Capítulos/Sinopse") {
    return CHAPTER_AND_EPISODE_ATTACHMENT_TYPES;
  }

  const dramaturgyNewSet = new Set();
  const varietiesNewSet = new Set();

  const DRAMATURGY_ATTACHMENT_TYPES_LIST =
    RENAMED_DRAMATURGY_ATTACHMENT_TYPES.filter((item) => {
      if (dramaturgyNewSet.has(item.id)) return false;
      dramaturgyNewSet.add(item.id);
      return true;
    });

  const VARIETIES_ATTACHMENT_TYPES_LIST =
    RENAMED_VARIETIES_ATTACHMENT_TYPES.filter((item) => {
      if (varietiesNewSet.has(item.id)) return false;
      varietiesNewSet.add(item.id);
      return true;
    }).map((item) => (item.id === 601 ? { ...item, id: 1211 } : item));

  const handleRenamedTypes = (contentType) => {
    const types = {
      dramaturgia: DRAMATURGY_ATTACHMENT_TYPES_LIST,
      variedades: VARIETIES_ATTACHMENT_TYPES_LIST,
    };

    return types[contentType];
  };

  const attachmentTypesList = handleRenamedTypes(contentType).sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
  );

  return attachmentTypesList;
};

export const returnNextValidBlockNumberToCreate = (list) => {
  if (!list?.length) return 1;

  const blockNumbers = list.map((block) => block.blockNumber);
  const maxBlockNumber = Math.max(...blockNumbers);

  return maxBlockNumber + 1;
};

export const buildDeleteAttachmentEndpoint = (attachment, parent) => {
  const isMaterialAttachment = !!parent?.type?.id;

  return isMaterialAttachment
    ? `planning/materials/${parent.id}/attachments/${attachment.id}`
    : `planning/blocks/${parent.blockId}/chapters/${parent.id}/attachments/${attachment.id}`;
};

export const collectAllAttachmentIds = (response) => {
  return [
    ...(response?.blocks?.flatMap(
      (block) =>
        block.chapters?.flatMap(
          (ch) => ch.attachments?.map((a) => a.attachmentId) ?? []
        ) ?? []
    ) ?? []),

    ...(response?.blocks?.flatMap(
      (block) =>
        block.chapters?.flatMap(
          (ch) =>
            ch.addenda?.map((a) => a.attachment?.id ?? a.attachmentId) ?? []
        ) ?? []
    ) ?? []),

    ...(response?.materials?.flatMap(
      (m) => m.attachments?.map((a) => a.id) ?? []
    ) ?? []),

    ...(response?.materials?.flatMap(
      (m) => m.addenda?.map((a) => a.attachment?.id ?? a.attachmentId) ?? []
    ) ?? []),
  ];
};

/**
 * Collects every chapter/episode an addendum (identified by its attachmentId) is
 * associated with across the planning response. The same attachmentId can appear
 * in multiple episodes/chapters, each with its own association id.
 *
 * @param {Object} planningData - The Planning/contents response
 * @param {number|string} attachmentId - The addendum's attachment id
 * @param {Object} current - The addendum entry the user acted on (for isCurrent)
 * @returns {Array<{ scope: "episode"|"chapter", number: number, isCurrent: boolean }>}
 */
export const collectAddendumAssociations = (
  planningData,
  attachmentId,
  current = {}
) => {
  const associations = [];
  const seen = new Set();

  const matchesAttachment = (addendum) =>
    (addendum?.attachment?.id ?? addendum?.attachmentId) === attachmentId;

  const push = (scope, number, isCurrent) => {
    if (number == null) return;
    const key = `${scope}-${number}`;
    if (seen.has(key)) return;
    seen.add(key);
    associations.push({ scope, number, isCurrent });
  };

  (planningData?.materials ?? []).forEach((material) => {
    if (Number(material?.type?.id) !== 1211) return;

    (material.addenda ?? []).forEach((addendum) => {
      if (!matchesAttachment(addendum)) return;
      const isCurrent =
        current?.episodeId != null && addendum.episodeId === current.episodeId;
      push("episode", material.episodeNumber, isCurrent);
    });
  });

  (planningData?.blocks ?? []).forEach((block) => {
    (block.chapters ?? []).forEach((chapter) => {
      (chapter.addenda ?? []).forEach((addendum) => {
        if (!matchesAttachment(addendum)) return;
        const isCurrent =
          current?.chapterId != null &&
          addendum.chapterId === current.chapterId;
        push("chapter", chapter.number, isCurrent);
      });
    });
  });

  return associations.sort((a, b) => a.number - b.number);
};

export const computeFormUpdate = (prev, inputName, value, materialsList) => {
  let updated = { ...prev };

  if (prev.planning && inputName in prev.planning) {
    updated.planning = {
      ...prev.planning,
      [inputName]: value,
    };
  } else {
    updated[inputName] = value;
  }

  if (inputName === "typeId") {
    if (value !== "1211") {
      updated.version = returnNextValidVersionNumber({
        versionsList: materialsList,
        typeId: Number(value),
      });
    } else {
      const nextEpisode = returnNextValidEpisodeNumber({
        versionsList: materialsList,
      });
      updated.episodeNumber = nextEpisode;
      updated.chapterNumber = nextEpisode;
      updated.version = returnNextValidVersionNumberForEpisodes({
        versionsList: materialsList,
        currentEpisodeNumber: nextEpisode,
      });
    }
  }

  if (inputName === "chapterNumber") {
    updated.version = returnNextValidVersionNumberForEpisodes({
      versionsList: materialsList,
      currentEpisodeNumber: Number(value),
    });
  }

  return updated;
};

/**
 * Returns the observation text from the most recent history entry, but only
 * when the attachment is currently released for the channel and that most
 * recent entry (by actionDate) is a "Channel" release.
 * @param {Object} releaseManagement
 * @returns {string}
 */
export const getLatestReleaseComment = (releaseManagement) => {
  if (!releaseManagement?.channel) return "";

  const history = releaseManagement?.history ?? [];

  const mostRecent = [...history].sort(
    (a, b) => new Date(b.actionDate) - new Date(a.actionDate)
  )[0];

  const isReleased =
    mostRecent?.type === "Channel" && mostRecent?.status === "Released";

  return isReleased ? (mostRecent.observation ?? "") : "";
};
